import { TextField } from "@mui/material";
import { isEqual } from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { TopicData, useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  Position,
} from "@/shared/state/visualization/schema/v1/three-d";

import { DataSourceSelector } from "../../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../../Panel/predicates";
import { ConfigItem } from "../../ConfigItem";
import styles from "../EntityConfig.module.css";

import { PositionPresetSelector } from "./PositionPresetSelector";
import { getFileAndPath, topicDataFor } from "./positionTopicData";
import { presetFor } from "./presets";

interface EntityConfigProps {
  entity: Entity;
  panelId: string;
}

export function PositionConfig({ entity, panelId }: EntityConfigProps) {
  const dispatch = useVizDispatch();

  // Track the most recent position, inclusive of updates originating from this component.
  // This is used to detect position updates that originated in other components or from
  // layout/workspace loads.
  const positionRef = React.useRef(entity.position);

  // A key used to reload the position form to match external updates.
  // This is done because the data source selector component only supports one-way updates after
  // initialization, but creating new selectors will reload the current state.
  const positionReloadKeyRef = React.useRef(0);

  // TODO(dave): look at using react-hook-form to manage all this state.
  // Position state
  const [forwardData, setForwardData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.position?.forward),
  );
  const [forwardScale, setForwardScale] = React.useState(
    `${entity.position?.forwardScale ?? 1}`,
  );
  const [forwardScaleError, setForwardScaleError] = React.useState("");

  const [rightData, setRightData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.position?.right),
  );
  const [rightScale, setRightScale] = React.useState(
    `${entity.position?.rightScale ?? 1}`,
  );
  const [rightScaleError, setRightScaleError] = React.useState("");

  const [upData, setUpData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.position?.up),
  );
  const [upScale, setUpScale] = React.useState(
    `${entity.position?.upScale ?? 1}`,
  );
  const [upScaleError, setUpScaleError] = React.useState("");
  const parseScaleInput = (
    value: string,
  ):
    | { ok: true; value: number; error?: undefined }
    | { ok: false; error: string } => {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return { ok: true, value: parsed };
    }
    return { ok: false, error: "Scale must be a number." };
  };

  const forwardConfig = getFileAndPath(entity.position?.forward);
  const rightConfig = getFileAndPath(entity.position?.right);
  const upConfig = getFileAndPath(entity.position?.up);

  React.useEffect(
    function onPositionFieldsChanged() {
      if (!isEqual(positionRef.current, entity.position)) {
        // entity.position was changed without this component's involvement.
        positionRef.current = entity.position;
        setForwardScale(`${entity.position?.forwardScale ?? ""}`);
        setRightScale(`${entity.position?.rightScale ?? ""}`);
        setUpScale(`${entity.position?.upScale ?? ""}`);

        setForwardData(topicDataFor(entity.position?.forward));
        setRightData(topicDataFor(entity.position?.right));
        setUpData(topicDataFor(entity.position?.up));
        // Trigger a reload of the forwarddata/rightdata/updata pickers
        // This is done because the data source selector component only supports one-way updates after
        // initialization, but creating new selectors will reload the current state.
        positionReloadKeyRef.current++;
        return;
      }
      // This component's state was changed -- apply the change to the entity.
      const parsedForwardScale = parseScaleInput(forwardScale);
      const parsedRightScale = parseScaleInput(rightScale);
      const parsedUpScale = parseScaleInput(upScale);
      setForwardScaleError(parsedForwardScale.error ?? "");
      setRightScaleError(parsedRightScale.error ?? "");
      setUpScaleError(parsedUpScale.error ?? "");
      if (!parsedForwardScale.ok || !parsedRightScale.ok || !parsedUpScale.ok) {
        return;
      }
      const updatedPosition: Position = {
        basePreset: entity.position?.basePreset,
        transformation: entity.position?.transformation,
        forward: forwardData ?? entity.position?.forward ?? 0,
        forwardScale: parsedForwardScale.value,
        right: rightData ?? entity.position?.right ?? 0,
        rightScale: parsedRightScale.value,
        up: upData ?? entity.position?.up ?? 0,
        upScale: parsedUpScale.value,
      };
      if (!isEqual(updatedPosition, entity.position)) {
        // Mark this update as originating from the component's form.
        positionRef.current = updatedPosition;
        // Run the update.
        dispatch(
          updateThreeDEntity(panelId, entity.id, {
            position: updatedPosition,
          }),
        );
      }
    },
    [
      forwardData,
      rightData,
      upData,
      forwardScale,
      rightScale,
      upScale,
      entity.position,
      entity.id,
      panelId,
      dispatch,
    ],
  );

  const presetLabels = presetFor(entity.position)?.labels;

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Position
      </RobotoTypography>
      <div className={styles.container}>
        <PositionPresetSelector entity={entity} panelId={panelId} />
        {entity.position?.basePreset && (
          <>
            <div key={positionReloadKeyRef.current}>
              <ConfigItem label={`${presetLabels?.right ?? "Right"} Axis`}>
                <DataSourceSelector
                  initialFile={rightConfig?.fileId}
                  initialPath={rightConfig?.path}
                  onChange={(node) => setRightData(node.toTopicData())}
                  // TODO(ENG-1672): use the 3d panel's viewability filter here
                  // once there's a filter to drag and drop message paths in.
                  optionFilter={isViewableAsPlot}
                />
              </ConfigItem>
              <ConfigItem label={`${presetLabels?.forward ?? "Forward"} Axis`}>
                <DataSourceSelector
                  initialFile={forwardConfig?.fileId}
                  initialPath={forwardConfig?.path}
                  onChange={(node) => setForwardData(node.toTopicData())}
                  optionFilter={isViewableAsPlot}
                />
              </ConfigItem>
              <ConfigItem label={`${presetLabels?.up ?? "Up"} Axis`}>
                <DataSourceSelector
                  initialFile={upConfig?.fileId}
                  initialPath={upConfig?.path}
                  onChange={(node) => setUpData(node.toTopicData())}
                  optionFilter={isViewableAsPlot}
                />
              </ConfigItem>
            </div>
            <ConfigItem label="Scaling">
              <div className={styles.fieldRow}>
                <TextField
                  label={`${presetLabels?.right ?? "Right"} Scale`}
                  type="number"
                  size="small"
                  margin="dense"
                  value={rightScale}
                  error={!!rightScaleError}
                  helperText={rightScaleError}
                  onChange={(e) => setRightScale(e.target.value)}
                  InputLabelProps={{ className: styles.input }}
                  InputProps={{ className: styles.input }}
                  inputProps={{ step: 0.1, className: styles.input }}
                  className={styles.scaleField}
                />
                <TextField
                  label={`${presetLabels?.forward ?? "Forward"} Scale`}
                  type="number"
                  size="small"
                  margin="dense"
                  value={forwardScale}
                  error={!!forwardScaleError}
                  helperText={forwardScaleError}
                  onChange={(e) => setForwardScale(e.target.value)}
                  InputLabelProps={{ className: styles.input }}
                  InputProps={{ className: styles.input }}
                  inputProps={{ step: 0.1, className: styles.input }}
                  className={styles.scaleField}
                />
                <TextField
                  label={`${presetLabels?.up ?? "Up"} Scale`}
                  type="number"
                  size="small"
                  margin="dense"
                  value={upScale}
                  error={!!upScaleError}
                  helperText={upScaleError}
                  onChange={(e) => setUpScale(e.target.value)}
                  InputLabelProps={{ className: styles.input }}
                  InputProps={{ className: styles.input }}
                  inputProps={{ step: 0.1, className: styles.input }}
                  className={styles.scaleField}
                />
              </div>
            </ConfigItem>
          </>
        )}
      </div>
    </>
  );
}
